import type {
  ApiResponse,
  DocDefinition,
  DocGroup,
  DocRevision,
  DocRevisionMeta,
  ReviewComment,
  Role,
} from "./types";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** 保存正文时的乐观锁冲突：正文已在别处被更新。 */
export class DocEditConflict extends ApiError {
  constructor(
    message: string,
    public readonly currentRelease: string,
  ) {
    super(message, 409);
    this.name = "DocEditConflict";
  }
}

async function parseResponse<T>(response: Response): Promise<ApiResponse<T>> {
  let data: ApiResponse<T>;
  try {
    data = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError("服务器返回了无法识别的内容", response.status);
  }
  if (!response.ok || !data.ok) {
    throw new ApiError(data.error || "请求失败", response.status);
  }
  return data;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<ApiResponse<T>> {
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    cache: "no-store",
    headers: {
      Accept: "application/json",
      ...(init.headers || {}),
    },
  });
  return parseResponse<T>(response);
}

export async function login(password: string): Promise<{ role: Role; admin: boolean }> {
  const data = await request("/api/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ password }),
  });
  return { role: data.role || "viewer", admin: !!data.admin };
}

export async function getSession(): Promise<{ ok: boolean; role: Role | null; admin: boolean }> {
  const response = await fetch("/api/me", {
    credentials: "same-origin",
    cache: "no-store",
    headers: { Accept: "application/json" },
  });
  const data = (await response.json()) as ApiResponse;
  return {
    ok: !!data.ok,
    role: data.ok ? data.role || (data.admin ? "admin" : "viewer") : null,
    admin: !!data.admin,
  };
}

export async function logout(): Promise<void> {
  await request("/api/logout", { method: "POST" });
}

export interface MarkdownDocument {
  text: string;
  /** 该文件的生效 release（X-Content-Release）；静态回退/vite dev 下为空串 */
  release: string;
}

export async function fetchMarkdown(path: string): Promise<MarkdownDocument> {
  const response = await fetch(path, {
    credentials: "same-origin",
    cache: "no-store",
  });
  if (!response.ok) {
    throw new ApiError(response.status === 401 ? "登录已失效" : "文档加载失败", response.status);
  }
  return {
    text: await response.text(),
    release: response.headers.get("X-Content-Release") ?? "",
  };
}

export async function saveDocEdit(payload: {
  path: string;
  content: string;
  base_release: string;
  note?: string;
}): Promise<{ release: string; revisionId: number | null; unchanged: boolean; historyWarning?: string }> {
  // 不走 request()：409 需要读取响应体里的 current_release。
  const response = await fetch("/api/doc-edit", {
    method: "POST",
    credentials: "same-origin",
    cache: "no-store",
    headers: { Accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  let data: ApiResponse;
  try {
    data = (await response.json()) as ApiResponse;
  } catch {
    throw new ApiError("服务器返回了无法识别的内容", response.status);
  }
  if (response.status === 409) {
    throw new DocEditConflict(data.error || "正文已在别处更新", data.current_release || "");
  }
  if (!response.ok || !data.ok) {
    throw new ApiError(data.error || "保存失败", response.status);
  }
  return {
    release: data.release || payload.base_release,
    revisionId: data.revision_id ?? null,
    unchanged: !!data.unchanged,
    ...(data.history_warning ? { historyWarning: data.history_warning } : {}),
  };
}

export async function fetchDocHistory(path: string): Promise<DocRevisionMeta[]> {
  const data = await request(`/api/doc-history?path=${encodeURIComponent(path)}`);
  return data.revisions || [];
}

export async function fetchDocRevision(path: string, id: number): Promise<DocRevision> {
  const data = await request(`/api/doc-history?path=${encodeURIComponent(path)}&id=${id}`);
  if (!data.revision) throw new ApiError("版本数据缺失", 500);
  return data.revision;
}

/** Load document catalog (includes auto-scanned chapters). */
export async function fetchCatalog(): Promise<DocDefinition[]> {
  const response = await fetch("/content/catalog.json", {
    credentials: "same-origin",
    cache: "no-store",
  });
  if (!response.ok) {
    throw new ApiError(
      response.status === 401 ? "登录已失效" : "目录加载失败",
      response.status,
    );
  }
  const data = (await response.json()) as {
    docs?: Array<{
      id: string;
      path: string;
      group: DocGroup;
      volume?: number | null;
      title: string;
      shortTitle: string;
      indexLabel: string;
    }>;
  };
  if (!Array.isArray(data.docs)) return [];
  return data.docs.map((doc) => ({
    id: doc.id,
    index: doc.indexLabel || "",
    title: doc.title,
    shortTitle: doc.shortTitle,
    file: `/content/${doc.path}`,
    group: doc.group,
    volume: doc.volume ?? undefined,
  }));
}

export async function fetchComments(docId?: string): Promise<ReviewComment[]> {
  const query = docId ? `?doc_id=${encodeURIComponent(docId)}` : "";
  const data = await request(`/api/comments${query}`);
  return data.comments || [];
}

export async function createComment(payload: {
  doc_id: string;
  body: string;
  author_name: string;
  quote_text?: string;
  quote_prefix?: string;
  quote_suffix?: string;
}): Promise<ReviewComment> {
  const data = await request("/api/comments", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!data.comment) throw new ApiError("评论写入后未返回数据", 500);
  return data.comment;
}

export async function updateComment(
  id: string,
  patch: {
    body?: string;
    author_name?: string;
    reply?: string;
    status?: "open" | "done";
  },
): Promise<ReviewComment> {
  const data = await request("/api/comments", {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ id, ...patch }),
  });
  if (!data.comment) throw new ApiError("评论更新后未返回数据", 500);
  return data.comment;
}

export async function deleteComment(id: string): Promise<void> {
  await request(`/api/comments?id=${encodeURIComponent(id)}`, { method: "DELETE" });
}
